import { computed, h } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import {
  Apps16,
  Bot16,
  Chat16,
  ChatBot16,
  Code16,
  Connect16,
  DataBase16,
  Flow16,
  Grid16,
  Group16,
  Integration16,
  Repeat16,
  Report16,
  Rule16,
  Security16,
  SettingsAdjust16,
  Tag16,
  Template16,
  Timer16,
  UserAdmin16,
  UserMultiple16,
  UserProfile16,
} from '@carbon/icons-vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import Icon from 'next/icon/Icon.vue';
import {
  CHANNEL_TYPES,
  channelTypeOfInbox,
} from 'dashboard/routes/dashboard/settings/inbox/channelTypes';

/**
 * The Settings menu (tier 2 of the Settings area), in one place: the sidebar shows it and the Settings Home
 * (phase 9.1, D-S1) draws a card per item from it, so a page, a permission or a flag is never listed twice.
 * Moved out of useSidebarMenu.js as it was, plus "Home" on top and the flag rule below.
 */
export function useSettingsMenu() {
  const { t } = useI18n();
  const router = useRouter();
  const route = useRoute();
  const getInbox = useMapGetter('inboxes/getInbox');
  const { accountScopedRoute } = useAccount();
  const accountId = useMapGetter('getCurrentAccountId');
  const isFeatureEnabledonAccount = useMapGetter(
    'accounts/isFeatureEnabledonAccount'
  );

  // X-5 / D-S4 (phase 9.1): in Settings a feature the account does not have is simply not offered, on any
  // installation. No upsell item and no plan lock on the page: the item goes away (the Home follows the menu).
  const hasFeature = flag =>
    isFeatureEnabledonAccount.value(accountId.value, flag);
  const accountHas = to => {
    const flag = router.resolve(to)?.meta?.featureFlag;
    return !flag || hasFeature(flag);
  };

  // The channel type the page is about: its own page, or the inbox the page shows.
  const INBOX_ROUTES = [
    'settings_inbox_show',
    'settings_inboxes_add_agents',
    'settings_inbox_finish',
  ];
  const activeChannelType = computed(() => {
    if (route.name === 'settings_channel_type') return route.params.channelType;
    if (!INBOX_ROUTES.includes(route.name)) return null;
    const inbox = getInbox.value(route.params.inboxId || route.params.inbox_id);
    return inbox?.id ? channelTypeOfInbox(inbox) : null;
  });

  const settingsGroup = (key, label, icon, children) => ({
    name: `settings-${key}`,
    label,
    icon,
    children: children.filter(child => accountHas(child.to)),
  });

  const settingsItems = computed(() => [
    // D-S1: the directory of every settings page, at the top like the kit's "Home".
    {
      name: 'settings-home',
      label: t('SETTINGS_HOME.TITLE'),
      icon: Grid16,
      to: accountScopedRoute('settings_home'),
      exact: true,
    },
    settingsGroup(
      'workspace',
      t('SIDEBAR.SETTINGS_GROUPS.WORKSPACE'),
      SettingsAdjust16,
      [
        {
          name: 'settings-account',
          label: t('SIDEBAR.ACCOUNT_SETTINGS'),
          icon: SettingsAdjust16,
          to: accountScopedRoute('general_settings_index'),
        },
        {
          name: 'settings-agents',
          label: t('SIDEBAR.AGENTS'),
          icon: UserMultiple16,
          to: accountScopedRoute('agent_list'),
        },
        {
          name: 'settings-teams',
          label: t('SIDEBAR.TEAMS'),
          icon: Group16,
          to: accountScopedRoute('settings_teams_list'),
          activeOn: [
            'settings_teams_list',
            'settings_teams_new',
            'settings_teams_finish',
            'settings_teams_add_agents',
            'settings_teams_show',
            'settings_teams_edit',
            'settings_teams_edit_members',
            'settings_teams_edit_finish',
          ],
        },
        {
          name: 'settings-roles',
          label: t('SIDEBAR.CUSTOM_ROLES'),
          icon: UserAdmin16,
          to: accountScopedRoute('custom_roles_list'),
        },
        {
          name: 'settings-security',
          label: t('SIDEBAR.SECURITY'),
          icon: Security16,
          to: accountScopedRoute('security_settings_index'),
        },
        {
          name: 'settings-audit',
          label: t('SIDEBAR.AUDIT_LOGS'),
          icon: Report16,
          to: accountScopedRoute('auditlogs_list'),
        },
      ]
    ),
    // D-S3 / R2-9-3 = A (phase 9.6): "All channels" and one page per channel type, always. A type is the current
    // item on its page and on the pages of an inbox of that type (detail, new-inbox steps).
    settingsGroup(
      'channels',
      t('SIDEBAR.SETTINGS_GROUPS.CHANNELS'),
      Connect16,
      [
        {
          name: 'settings-channels-all',
          label: t('SETTINGS_CHANNELS.TYPES.ALL.NAME'),
          sentence: t('SETTINGS_CHANNELS.TYPES.ALL.SENTENCE'),
          icon: Apps16,
          to: accountScopedRoute('settings_channels'),
          activeOn: [
            'settings_channels',
            'settings_inbox_new',
            'settings_inboxes_page_channel',
          ],
        },
        ...CHANNEL_TYPES.map(type => ({
          name: `settings-channels-${type.key}`,
          label: t(`SETTINGS_CHANNELS.TYPES.${type.key.toUpperCase()}.NAME`),
          sentence: t(
            `SETTINGS_CHANNELS.TYPES.${type.key.toUpperCase()}.SENTENCE`
          ),
          // A component, not a vnode: this computed is first read while the shell renders, and a vnode built
          // here leaks into that render block (Vue then patches an Icon that was never mounted: "subTree of null").
          icon: () => h(Icon, { icon: type.icon, class: 'size-4' }),
          to: accountScopedRoute('settings_channel_type', {
            channelType: type.key,
          }),
          isActive: () => activeChannelType.value === type.key,
        })),
      ]
    ),
    settingsGroup('inbox', t('SIDEBAR.SETTINGS_GROUPS.INBOX'), Chat16, [
      {
        name: 'settings-labels',
        label: t('SIDEBAR.LABELS'),
        icon: Tag16,
        to: accountScopedRoute('labels_list'),
      },
      {
        name: 'settings-attributes',
        label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
        icon: Code16,
        to: accountScopedRoute('attributes_list'),
      },
      {
        name: 'settings-canned',
        label: t('SIDEBAR.CANNED_RESPONSES'),
        icon: Template16,
        to: accountScopedRoute('canned_list'),
      },
      {
        name: 'settings-macros',
        label: t('SIDEBAR.MACROS'),
        icon: Apps16,
        to: accountScopedRoute('macros_wrapper'),
      },
      {
        name: 'settings-workflow',
        label: t('SIDEBAR.CONVERSATION_WORKFLOW'),
        icon: Flow16,
        to: accountScopedRoute('conversation_workflow_index'),
      },
      {
        name: 'settings-sla',
        label: t('SIDEBAR.SLA'),
        icon: Timer16,
        to: accountScopedRoute('sla_list'),
      },
      ...(hasFeature(FEATURE_FLAGS.ADVANCED_ASSIGNMENT)
        ? [
            {
              name: 'settings-assignment',
              label: t('SIDEBAR.AGENT_ASSIGNMENT'),
              icon: Rule16,
              to: accountScopedRoute('assignment_policy_index'),
              activeOn: [
                'assignment_policy_index',
                'agent_assignment_policy_index',
                'agent_assignment_policy_create',
                'agent_assignment_policy_edit',
                'agent_capacity_policy_index',
                'agent_capacity_policy_create',
                'agent_capacity_policy_edit',
              ],
            },
          ]
        : []),
    ]),
    settingsGroup(
      'ai_automation',
      t('SIDEBAR.SETTINGS_GROUPS.AI_AUTOMATION'),
      Bot16,
      [
        {
          name: 'settings-polaris',
          label: t('SIDEBAR.POLARIS_AI'),
          icon: Bot16,
          to: accountScopedRoute('polaris_settings_index'),
        },
        {
          name: 'settings-automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: Repeat16,
          to: accountScopedRoute('automation_list'),
        },
        {
          name: 'settings-bots',
          label: t('SIDEBAR.AGENT_BOTS'),
          icon: ChatBot16,
          to: accountScopedRoute('agent_bots'),
        },
      ]
    ),
    settingsGroup(
      'integrations',
      t('SIDEBAR.SETTINGS_GROUPS.INTEGRATIONS'),
      Integration16,
      [
        {
          name: 'settings-integrations',
          label: t('SIDEBAR.INTEGRATIONS'),
          icon: Integration16,
          to: accountScopedRoute('settings_applications'),
        },
      ]
    ),
    ...(hasFeature(FEATURE_FLAGS.DATA_IMPORT)
      ? [
          settingsGroup('data', t('SIDEBAR.SETTINGS_GROUPS.DATA'), DataBase16, [
            {
              name: 'settings-data',
              label: t('SIDEBAR.DATA'),
              icon: DataBase16,
              to: accountScopedRoute('settings_data_imports'),
            },
          ]),
        ]
      : []),
    settingsGroup(
      'personal',
      t('SIDEBAR.SETTINGS_GROUPS.PERSONAL'),
      UserProfile16,
      [
        {
          name: 'settings-profile',
          label: t('SIDEBAR.PROFILE_SETTINGS'),
          icon: UserProfile16,
          to: accountScopedRoute('profile_settings_index'),
          activeOn: ['profile_settings_index', 'profile_settings_mfa'],
        },
      ]
    ),
  ]);

  return { settingsItems };
}
